import React, { useEffect, useState } from 'react';
import { Shield, CheckCircle, Clock, User, ArrowRight } from 'lucide-react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface Props {
  tenantId: string;
}

interface ApprovalEvent {
  id: string;
  staffName: string;
  staffEmail: string;
  amount: number;
  approvedBy: string;
  timestamp: any;
  source: string;
  type: string;
  tipDate: string;
  shiftName: string;
  shiftStartTime: string;
  shiftEndTime: string;
  posId: string;
  batchId: string;
}

export const AuditLogDashboard: React.FC<Props> = ({ tenantId }) => {
  const [events, setEvents] = useState<ApprovalEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;

    const fetchLogs = async () => {
      try {
        const q = query(
          collection(db, 'tenants', tenantId, 'master_transactions'),
          where('submissionStatus', '==', 'Approved'),
          orderBy('auditTimestamp', 'desc'),
          limit(20)
        );
        
        const snapshot = await getDocs(q);
        const logs = snapshot.docs.map(doc => ({
          id: doc.id,
          staffName: doc.data().staffName || 'Unknown',
          staffEmail: doc.data().staffEmail || doc.data().email || 'N/A',
          amount: doc.data().amount || doc.data().allocatedAmount || 0,
          approvedBy: doc.data().auditActor || 'System',
          timestamp: doc.data().auditTimestamp || doc.data().timestamp,
          source: doc.data().source || 'N/A',
          type: doc.data().type || 'N/A',
          tipDate: doc.data().tipDate || 'N/A',
          shiftName: doc.data().shiftName || 'N/A',
          shiftStartTime: doc.data().shiftStartTime || 'N/A',
          shiftEndTime: doc.data().shiftEndTime || 'N/A',
          posId: doc.data().posId || doc.data().source_id || 'N/A',
          batchId: doc.data().batchId || 'N/A'
        }));
        
        setEvents(logs);
      } catch (error) {
        console.error("Error fetching approval logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [tenantId]);

  return (
    <div className="space-y-6">
      <div className="card p-6 bg-white border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
            <Shield size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 uppercase tracking-tight">POS Import Approval History</h3>
            <p className="text-sm text-gray-500">Historical record of all approved POS Import transactions.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : events.length > 0 ? (
          <div className="space-y-4">
            {events.map((event) => (
              <div key={event.id} className="p-5 bg-gray-50 rounded-xl border border-gray-100 transition-hover hover:border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-900">{event.staffName}</span>
                    <span className="text-xs text-gray-500">{event.staffEmail}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-black text-emerald-600">${event.amount.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-[10px] uppercase font-black text-gray-500 tracking-wider">
                  <div>
                    <span className="block text-gray-400 font-bold">Tip Date</span>
                    <span className="text-gray-900">{event.tipDate}</span>
                  </div>
                  <div>
                    <span className="block text-gray-400 font-bold">Shift</span>
                    <span className="text-gray-900">{event.shiftName} ({event.shiftStartTime}-{event.shiftEndTime})</span>
                  </div>
                  <div>
                    <span className="block text-gray-400 font-bold">POS ID</span>
                    <span className="text-gray-900">{event.posId}</span>
                  </div>
                  <div>
                    <span className="block text-gray-400 font-bold">Batch ID</span>
                    <span className="text-gray-900">{event.batchId}</span>
                  </div>
                  <div>
                    <span className="block text-gray-400 font-bold">Approved By</span>
                    <span className="text-gray-900">{event.approvedBy}</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded font-black uppercase tracking-tighter">{event.source}</span>
                    <span className="text-[10px] text-gray-400 font-mono tracking-tighter">
                      {event.timestamp?.toDate().toLocaleString()}
                    </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-10 text-center text-gray-400 font-medium italic border-2 border-dashed border-gray-100 rounded-xl">
            No POS Import approval events logged yet.
          </div>
        )}
      </div>
    </div>
  );
};
